import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Car, Package, ShoppingBag, Pill, ArrowRight, CheckCircle, MapPin, Circle, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import ServiceAreaMap from '../components/ServiceAreaMap';
import CoverageChecker from '../components/CoverageChecker';
import { hubAPI } from '../lib/api';
import { devError } from '../lib/devLog';

const Landing = () => {
  const [hubs, setHubs] = useState([]);
  const [focusedHubId, setFocusedHubId] = useState(null);
  const primaryHub = hubs.find(h => h.is_primary) || hubs[0] || null;
  const primaryHubName = primaryHub?.name || 'Mascoutah';
  const primaryHubState = primaryHub?.state || 'IL';

  useEffect(() => {
    hubAPI.list()
      .then(res => setHubs(res.data || []))
      .catch(err => devError('Failed to fetch hubs:', err));
  }, []);

  const services = [
    { icon: Car, title: 'Rides', description: 'Get where you need to go', price: '$15.00' },
    { icon: Package, title: 'Packages', description: 'Send items across town', price: '$12.00' },
    { icon: ShoppingBag, title: 'Groceries', description: 'Fresh groceries delivered', price: '$10.00' },
    { icon: Pill, title: 'Pharmacy', description: 'Medicine to your door', price: '$7.00' },
  ];

  const zones = [
    { name: 'Hub', description: hubs.length > 0 ? hubs.map(h => h.name).join(', ') : 'Mascoutah', radius: '0-5 mi', addon: '$0.00', color: 'bg-orange-500' },
    { name: 'Zone 1', description: 'New Baden, Freeburg', radius: '5-10 mi', addon: '+$2.00', color: 'bg-blue-500' },
    { name: 'Zone 2', description: "O'Fallon, Shiloh", radius: '10-15 mi', addon: '+$4.00', color: 'bg-green-500' },
    { name: 'Zone 3', description: 'Belleville, Scott AFB', radius: '15-25 mi', addon: '+$11.00', color: 'bg-purple-500' },
    { name: 'Zone 4', description: 'Collinsville, Edwardsville', radius: '25-35 mi', addon: '+$15.00', color: 'bg-pink-500' },
    { name: 'Zone 5', description: 'STL Airport, Busch Stadium', radius: '35-45 mi', addon: '+$25.00', color: 'bg-red-500' },
  ];

  const features = [
    'Fixed transparent pricing',
    'No surge pricing ever',
    'Local drivers you can trust',
    'Hub-based service model',
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-header border-b border-zinc-200">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
              <Car className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>LocalMove</span>
          </Link>
          
          <nav className="hidden md:flex items-center gap-8">
            <a href="#services" className="text-zinc-600 hover:text-zinc-900 transition-colors">Services</a>
            <a href="#zones" className="text-zinc-600 hover:text-zinc-900 transition-colors">Service Area</a>
            <a href="#drivers" className="text-zinc-600 hover:text-zinc-900 transition-colors">Driver Earnings</a>
            <Link to="/driver/register" className="text-zinc-600 hover:text-zinc-900 transition-colors">Become a Driver</Link>
          </nav>
          
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-zinc-700" data-testid="login-btn">Log in</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6" data-testid="signup-btn">
                Sign up
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium">
                <MapPin className="w-4 h-4" />
                {hubs.length > 1
                  ? `Serving ${hubs.length} hubs across the Metro East / Greater St. Louis region`
                  : `Serving ${primaryHubName}, ${primaryHubState} & surrounding areas`}
              </div>
              <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight text-zinc-900 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Local rides & deliveries,{' '}
                <span className="text-orange-500">made simple</span>
              </h1>
              <p className="text-lg text-zinc-600 leading-relaxed max-w-lg">
                Get around town or have items delivered with fixed, transparent pricing.
                Hub-based service across the Metro East region — from your neighborhood to downtown St. Louis.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-6 text-lg btn-scale" data-testid="hero-get-started-btn">
                    Get Started <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/driver/register">
                  <Button size="lg" variant="outline" className="rounded-full px-8 py-6 text-lg border-zinc-300" data-testid="hero-drive-btn">
                    Drive with us
                  </Button>
                </Link>
              </div>
              <div className="flex flex-wrap gap-4 pt-4">
                {features.map((feature, index) => (
                  <div key={feature} className="flex items-center gap-2 text-zinc-600">
                    <CheckCircle className="w-5 h-5 text-orange-500" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1642006891272-9f8237d69079?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHx3b21hbiUyMHNtYXJ0cGhvbmUlMjBwb3JjaCUyMGhvbWUlMjBzdW5ueXxlbnwwfHx8fDE3NzExNDI4MjV8MA&ixlib=rb-4.1.0&q=85"
                  alt="Neighbors relaxing on porch"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-zinc-900">Fixed Pricing</p>
                  <p className="text-sm text-zinc-500">No surprises</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-6 bg-zinc-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Our Services
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              Whether you need a ride or a delivery, we've got you covered with fixed base pricing plus zone-based add-ons.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => (
              <div 
                key={service.title}
                className="bg-white rounded-2xl p-6 border border-zinc-200 card-hover cursor-pointer"
                data-testid={`service-card-${service.title.toLowerCase()}`}
              >
                <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                  <service.icon className="w-7 h-7 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {service.title}
                </h3>
                <p className="text-zinc-600 mb-4">{service.description}</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-orange-500">{service.price}</span>
                  <span className="text-zinc-500 text-sm">base + zone</span>
                </div>
              </div>
            ))}
          </div>

          {/* Detailed Pricing Table */}
          <div className="mt-16 bg-white rounded-2xl border border-zinc-200 overflow-hidden shadow-lg">
            <div className="bg-orange-500 text-white p-6">
              <h3 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                📋 Complete Pricing Guide
              </h3>
              <p className="text-orange-100 mt-2">Total Price = Base Rate + Zone Add-on</p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-zinc-700">Service</th>
                    <th className="px-4 py-3 text-center font-semibold text-zinc-700">Base</th>
                    <th className="px-4 py-3 text-center font-semibold text-orange-600">Hub<br/><span className="font-normal text-xs">0-5 mi</span></th>
                    <th className="px-4 py-3 text-center font-semibold text-blue-600">Zone 1<br/><span className="font-normal text-xs">+$2</span></th>
                    <th className="px-4 py-3 text-center font-semibold text-green-600">Zone 2<br/><span className="font-normal text-xs">+$4</span></th>
                    <th className="px-4 py-3 text-center font-semibold text-purple-600">Zone 3<br/><span className="font-normal text-xs">+$11</span></th>
                    <th className="px-4 py-3 text-center font-semibold text-pink-600">Zone 4<br/><span className="font-normal text-xs">+$15</span></th>
                    <th className="px-4 py-3 text-center font-semibold text-red-600">Zone 5<br/><span className="font-normal text-xs">+$25</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  <tr className="hover:bg-zinc-50">
                    <td className="px-4 py-3 font-medium flex items-center gap-2">
                      <Car className="w-4 h-4 text-orange-500" /> Ride
                    </td>
                    <td className="px-4 py-3 text-center text-zinc-600">$15.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-orange-600">$15.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-blue-600">$17.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-green-600">$19.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-purple-600">$26.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-pink-600">$30.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-red-600">$40.00</td>
                  </tr>
                  <tr className="hover:bg-zinc-50">
                    <td className="px-4 py-3 font-medium flex items-center gap-2">
                      <ShoppingBag className="w-4 h-4 text-orange-500" /> Food
                    </td>
                    <td className="px-4 py-3 text-center text-zinc-600">$8.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-orange-600">$8.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-blue-600">$10.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-green-600">$12.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-purple-600">$19.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-pink-600">$23.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-red-600">$33.00</td>
                  </tr>
                  <tr className="hover:bg-zinc-50">
                    <td className="px-4 py-3 font-medium flex items-center gap-2">
                      <ShoppingBag className="w-4 h-4 text-orange-500" /> Grocery
                    </td>
                    <td className="px-4 py-3 text-center text-zinc-600">$10.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-orange-600">$10.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-blue-600">$12.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-green-600">$14.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-purple-600">$21.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-pink-600">$25.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-red-600">$35.00</td>
                  </tr>
                  <tr className="hover:bg-zinc-50">
                    <td className="px-4 py-3 font-medium flex items-center gap-2">
                      <Pill className="w-4 h-4 text-orange-500" /> Pharmacy
                    </td>
                    <td className="px-4 py-3 text-center text-zinc-600">$7.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-orange-600">$7.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-blue-600">$9.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-green-600">$11.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-purple-600">$18.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-pink-600">$22.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-red-600">$32.00</td>
                  </tr>
                  <tr className="hover:bg-zinc-50">
                    <td className="px-4 py-3 font-medium flex items-center gap-2">
                      <Package className="w-4 h-4 text-orange-500" /> Package
                    </td>
                    <td className="px-4 py-3 text-center text-zinc-600">$12.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-orange-600">$12.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-blue-600">$14.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-green-600">$16.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-purple-600">$23.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-pink-600">$27.00</td>
                    <td className="px-4 py-3 text-center font-semibold text-red-600">$37.00</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="bg-zinc-50 p-4 border-t border-zinc-200">
              <div className="grid md:grid-cols-3 gap-4 text-sm text-zinc-600">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-orange-500 mt-0.5" />
                  <div>
                    <strong>Hub (0-5 mi)</strong>
                    <p className="text-xs">{hubs.length > 0 ? hubs.map(h => h.name).join(', ') : 'Mascoutah'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Circle className="w-4 h-4 text-blue-500 mt-0.5" />
                  <div>
                    <strong>Zone 1 (5-10 mi)</strong>
                    <p className="text-xs">New Baden, Freeburg, Lebanon, Trenton</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Circle className="w-4 h-4 text-green-500 mt-0.5" />
                  <div>
                    <strong>Zone 2 (10-15 mi)</strong>
                    <p className="text-xs">O'Fallon, Shiloh, Scott AFB, Millstadt</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Circle className="w-4 h-4 text-purple-500 mt-0.5" />
                  <div>
                    <strong>Zone 3 (15-25 mi)</strong>
                    <p className="text-xs">Belleville, Swansea, Fairview Heights</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Circle className="w-4 h-4 text-pink-500 mt-0.5" />
                  <div>
                    <strong>Zone 4 (25-35 mi)</strong>
                    <p className="text-xs">Collinsville, Glen Carbon, Edwardsville</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Circle className="w-4 h-4 text-red-500 mt-0.5" />
                  <div>
                    <strong>Zone 5 (35-45 mi)</strong>
                    <p className="text-xs">STL Airport, Alton, Downtown St. Louis</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Zones Section */}
      <section id="zones" className="py-20 px-6">
        <div className="container mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Where We Operate
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              {hubs.length > 1
                ? `LocalMove operates from ${hubs.length} service hubs across the Metro East region. Pricing is the base service rate plus a small zone add-on based on distance from the nearest hub.`
                : 'All rides and deliveries operate through our service hub. Pricing is the base service rate plus a small zone add-on based on distance.'}
            </p>
          </div>

          {/* Coverage checker */}
          <CoverageChecker onFocusHub={setFocusedHubId} />

          {/* Dynamic Hub Pills */}
          {hubs.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-2 mb-12 max-w-3xl mx-auto" data-testid="hub-pills">
              <button
                type="button"
                onClick={() => setFocusedHubId(null)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                  focusedHubId === null
                    ? 'bg-zinc-900 border-zinc-900 text-white shadow-md'
                    : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400'
                }`}
                data-testid="hub-pill-all"
              >
                All hubs
              </button>
              {[...hubs]
                .sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0))
                .map(hub => {
                  const isSelected = focusedHubId === hub.id;
                  return (
                    <button
                      key={hub.id || hub.name}
                      type="button"
                      onClick={() => setFocusedHubId(hub.id)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all cursor-pointer ${
                        hub.is_primary
                          ? isSelected
                            ? 'bg-orange-500 border-orange-500 text-white shadow-md scale-105'
                            : 'bg-orange-50 border-orange-200 text-orange-800 hover:border-orange-400 hover:shadow-sm'
                          : isSelected
                          ? 'bg-violet-500 border-violet-500 text-white shadow-md scale-105'
                          : 'bg-violet-50 border-violet-200 text-violet-800 hover:border-violet-400 hover:shadow-sm'
                      }`}
                      data-testid={`hub-pill-${(hub.name || '').toLowerCase().replace(/\s+/g, '-')}`}
                      title={`Focus map on ${hub.name}`}
                    >
                      {hub.is_primary ? (
                        <Star className={`w-3.5 h-3.5 ${isSelected ? 'fill-white text-white' : 'fill-orange-500 text-orange-500'}`} />
                      ) : (
                        <MapPin className="w-3.5 h-3.5" />
                      )}
                      {hub.name}
                      <span className="opacity-60 text-xs">· {hub.state || 'IL'}</span>
                    </button>
                  );
                })}
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Service Area Map */}
            <div className="relative">
              <ServiceAreaMap height="500px" className="shadow-xl" focusedHubId={focusedHubId} />
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white px-4 py-2 rounded-full shadow-md border border-zinc-200">
                <p className="text-sm font-medium text-zinc-900">
                  {focusedHubId && hubs.find(h => h.id === focusedHubId)
                    ? `${hubs.find(h => h.id === focusedHubId).name}, ${hubs.find(h => h.id === focusedHubId).state || 'IL'} - Service Hub`
                    : hubs.length > 1
                    ? `${hubs.length} hubs · Metro East, IL`
                    : `${primaryHubName}, ${primaryHubState} - Service Hub`}
                </p>
              </div>
            </div>

            {/* Zone List */}
            <div className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
                <p className="text-orange-800 text-sm">
                  <strong>How it works:</strong> Your job is routed through the nearest active hub.
                  Total price = base service rate + a small zone add-on for the non-hub location.
                </p>
              </div>
              
              {zones.map((zone, index) => (
                <div 
                  key={zone.name}
                  className="bg-white border border-zinc-200 rounded-xl p-4 flex items-center justify-between hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 ${zone.color} rounded-full flex items-center justify-center`}>
                      {index === 0 ? (
                        <MapPin className="w-5 h-5 text-white" />
                      ) : (
                        <Circle className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-semibold text-zinc-900">{zone.name}</h4>
                      <p className="text-sm text-zinc-500">{zone.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-zinc-900">{zone.addon}</p>
                    <p className="text-xs text-zinc-500">{zone.radius}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Driver CTA Section */}
      <section id="drivers" className="py-20 px-6 bg-zinc-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Drive with LocalMove
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              Keep more of what you earn. No hidden fees, no surprises. See exactly what you'll make on every job.
            </p>
          </div>

          {/* Driver Earnings Highlight */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white text-center">
              <p className="text-5xl font-bold mb-2">80%</p>
              <p className="text-green-100">You Keep</p>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 text-white text-center">
              <p className="text-5xl font-bold mb-2">20%</p>
              <p className="text-orange-100">Platform Fee</p>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white text-center">
              <p className="text-5xl font-bold mb-2">$0</p>
              <p className="text-blue-100">Hidden Fees</p>
            </div>
          </div>

          {/* Driver Earnings Table */}
          <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden shadow-lg mb-12">
            <div className="bg-green-600 text-white p-6">
              <h3 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                💰 Driver Earnings
              </h3>
              <p className="text-green-100 mt-2">Driver Pay = 80% of Total Fare (Platform keeps 20%)</p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-zinc-700">Service</th>
                    <th className="px-4 py-3 text-center font-semibold text-orange-600">Hub<br/><span className="font-normal text-xs">0-5 mi</span></th>
                    <th className="px-4 py-3 text-center font-semibold text-blue-600">Zone 1<br/><span className="font-normal text-xs">New Baden</span></th>
                    <th className="px-4 py-3 text-center font-semibold text-green-600">Zone 2<br/><span className="font-normal text-xs">O'Fallon</span></th>
                    <th className="px-4 py-3 text-center font-semibold text-purple-600">Zone 3<br/><span className="font-normal text-xs">Belleville</span></th>
                    <th className="px-4 py-3 text-center font-semibold text-pink-600">Zone 4<br/><span className="font-normal text-xs">Edwardsville</span></th>
                    <th className="px-4 py-3 text-center font-semibold text-red-600">Zone 5<br/><span className="font-normal text-xs">STL Airport</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  <tr className="hover:bg-green-50">
                    <td className="px-4 py-3 font-medium flex items-center gap-2">
                      <Car className="w-4 h-4 text-green-600" /> Ride
                    </td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$12.00</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$13.60</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$15.20</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$20.80</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$24.00</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$32.00</td>
                  </tr>
                  <tr className="hover:bg-green-50">
                    <td className="px-4 py-3 font-medium flex items-center gap-2">
                      <ShoppingBag className="w-4 h-4 text-green-600" /> Food
                    </td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$6.40</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$8.00</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$9.60</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$15.20</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$18.40</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$26.40</td>
                  </tr>
                  <tr className="hover:bg-green-50">
                    <td className="px-4 py-3 font-medium flex items-center gap-2">
                      <ShoppingBag className="w-4 h-4 text-green-600" /> Grocery
                    </td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$8.00</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$9.60</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$11.20</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$16.80</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$20.00</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$28.00</td>
                  </tr>
                  <tr className="hover:bg-green-50">
                    <td className="px-4 py-3 font-medium flex items-center gap-2">
                      <Pill className="w-4 h-4 text-green-600" /> Pharmacy
                    </td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$5.60</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$7.20</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$8.80</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$14.40</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$17.60</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$25.60</td>
                  </tr>
                  <tr className="hover:bg-green-50">
                    <td className="px-4 py-3 font-medium flex items-center gap-2">
                      <Package className="w-4 h-4 text-green-600" /> Package
                    </td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$9.60</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$11.20</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$12.80</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$18.40</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$21.60</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">$29.60</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="bg-green-50 p-4 border-t border-green-200">
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-zinc-700">Weekly payouts</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-zinc-700">No minimum hours</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-zinc-700">Keep 100% of tips</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-zinc-700">Flexible schedule</span>
                </div>
              </div>
            </div>
          </div>

          {/* Example Earnings Card */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-2xl border border-zinc-200 p-6">
              <h4 className="font-bold text-lg text-zinc-900 mb-4">📊 Example: Part-Time Driver</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-zinc-100">
                  <span className="text-zinc-600">Hours per week</span>
                  <span className="font-semibold">15 hours</span>
                </div>
                <div className="flex justify-between py-2 border-b border-zinc-100">
                  <span className="text-zinc-600">Avg jobs per hour</span>
                  <span className="font-semibold">2 jobs</span>
                </div>
                <div className="flex justify-between py-2 border-b border-zinc-100">
                  <span className="text-zinc-600">Avg earning per job</span>
                  <span className="font-semibold">$14.00</span>
                </div>
                <div className="flex justify-between py-2 bg-green-50 rounded-lg px-3 mt-2">
                  <span className="font-semibold text-green-800">Weekly Earnings</span>
                  <span className="font-bold text-green-600 text-lg">$420</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl border border-zinc-200 p-6">
              <h4 className="font-bold text-lg text-zinc-900 mb-4">📊 Example: Full-Time Driver</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-zinc-100">
                  <span className="text-zinc-600">Hours per week</span>
                  <span className="font-semibold">40 hours</span>
                </div>
                <div className="flex justify-between py-2 border-b border-zinc-100">
                  <span className="text-zinc-600">Avg jobs per hour</span>
                  <span className="font-semibold">2.5 jobs</span>
                </div>
                <div className="flex justify-between py-2 border-b border-zinc-100">
                  <span className="text-zinc-600">Avg earning per job</span>
                  <span className="font-semibold">$14.00</span>
                </div>
                <div className="flex justify-between py-2 bg-green-50 rounded-lg px-3 mt-2">
                  <span className="font-semibold text-green-800">Weekly Earnings</span>
                  <span className="font-bold text-green-600 text-lg">$1,400</span>
                </div>
              </div>
            </div>
          </div>

          {/* Driver CTA Card */}
          <div className="bg-zinc-900 rounded-3xl p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center overflow-hidden relative">
            <div className="space-y-6 relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Ready to Start Earning?
              </h2>
              <p className="text-zinc-400 text-lg">
                Join our growing network of drivers across the Metro East region. Sign up takes just 5 minutes.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/driver/register">
                  <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8" data-testid="become-driver-btn">
                    Become a Driver <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="outline" className="border-zinc-600 text-white hover:bg-zinc-800 rounded-full px-8">
                    Driver Login
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1659353741091-e0274bb50905?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxkZWxpdmVyeSUyMGRyaXZlciUyMHNtaWxpbmclMjBib3glMjBkb29yc3RlcCUyMGZyaWVuZGx5fGVufDB8fHx8MTc3MTE0MjgzNHww&ixlib=rb-4.1.0&q=85"
                alt="Friendly delivery driver with packages"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-zinc-200 py-12 px-6">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl tracking-tight text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>LocalMove</span>
            </div>
            <p className="text-zinc-500 text-sm">
              © 2026 LocalMove. Serving the Metro East / Greater St. Louis region.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
